import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { appActions } from 'in-meeting-experience-ui-components/contexts/app/context';
import { actions as errorActions } from 'in-meeting-experience-ui-components/contexts/error/context';
import { showApiAlert } from 'in-meeting-experience-ui-components/utils/alerts';
import { formatParticipant } from 'in-meeting-experience-ui-components/utils/contacts';
import { isDataOutdated } from 'in-meeting-experience-ui-components/utils/helpers';
import { getSessionStorageItem, setSessionStorageItem } from 'in-meeting-experience-ui-components/utils/sessionStotage';
import { AUTOMAPPED_PARTICIPANTS_EVENT_SENT_KEY } from '../../constants/google-meet';
import { ACTIONS, SCREENS } from '../../constants/tracking';
import { transformGoogleParticipants } from '../../utils/participants';
import { meetingUUIDSelector, googleConferenceRecordIdSelector } from '../google/selectors';
import { trackerActions } from '../tracker/slice';
import { meetingParticipantsSelector } from './selectors';
export const setParticipants = createAction('participants/setParticipants');
export const getMeetingParticipantsFromBackend = createAsyncThunk('participants/getMeetingParticipantsFromBackend', (_, thunkAPI) => {
  const meetingUUID = meetingUUIDSelector(thunkAPI.getState()) || '';
  return thunkAPI.extra.participantsApi.fetchMeetingParticipants({
    meetingUid: meetingUUID
  }).then(context => {
    thunkAPI.dispatch(setParticipants(context.map(participant => formatParticipant(participant))));
  }).catch(error => thunkAPI.rejectWithValue(error));
});
export const saveMeetingParticipants = createAsyncThunk('participants/saveMeetingParticipants', ({
  participants,
  intervalFetchAction
}, thunkAPI) => {
  const meetingUUID = meetingUUIDSelector(thunkAPI.getState()) || '';
  return thunkAPI.extra.participantsApi.saveMeetingParticipants({
    data: participants.map(participant => ({
      participantId: participant.participantUUID || participant.participantId,
      userName: participant.screenName,
      joinedAt: Date.now(),
      meetingUid: meetingUUID
    })),
    meetingUid: meetingUUID
  }).then(response => {
    if (response.some(participant => participant.isAutoMapped) && !getSessionStorageItem(AUTOMAPPED_PARTICIPANTS_EVENT_SENT_KEY)) {
      setSessionStorageItem(AUTOMAPPED_PARTICIPANTS_EVENT_SENT_KEY, true);
      thunkAPI.dispatch(trackerActions.trackUsage(Object.assign({
        screen: SCREENS.participants
      }, ACTIONS.participants.autoMap)));
    }
    if (intervalFetchAction) {
      appActions.postponeWithAppLoader(() => thunkAPI.dispatch(setParticipants(response.map(formatParticipant))));
    } else {
      thunkAPI.dispatch(setParticipants(response.map(formatParticipant)));
    }
  }).catch(error => {
    showApiAlert(error);
    return thunkAPI.rejectWithValue(error);
  });
});
export const mapParticipantToContact = createAsyncThunk('participants/mapParticipantToContact', ({
  contactId,
  participantId
}, thunkAPI) => {
  const meetingUUID = meetingUUIDSelector(thunkAPI.getState()) || '';
  return thunkAPI.extra.participantsApi.mapParticipantToContact({
    participantId,
    contactId,
    meetingUid: meetingUUID
  }).then(response => {
    thunkAPI.dispatch(trackerActions.trackUsage(Object.assign({
      screen: SCREENS.associateContact
    }, ACTIONS.associateContact.map)));
    return {
      participantId: response.participantId,
      contact: response.contact
    };
  }).catch(error => {
    showApiAlert(error);
    return thunkAPI.rejectWithValue(error);
  });
});
export const getMeetingParticipants = createAsyncThunk('participants/getMeetingParticipants', (payload = {
  intervalFetchAction: false
}, thunkAPI) => {
  const conferenceRecordId = googleConferenceRecordIdSelector(thunkAPI.getState()) || '';
  const storeParticipants = meetingParticipantsSelector(thunkAPI.getState());
  return thunkAPI.extra.googleApi.getParticipants(conferenceRecordId, payload === null || payload === void 0 ? void 0 : payload.setSkipRefresh).then(response => {
    const transformedParticipants = transformGoogleParticipants(response);
    const isDataChanged = isDataOutdated(transformedParticipants, storeParticipants, (a, b) => a.participantId === b.participantId);
    if (!isDataChanged) return response;
    thunkAPI.dispatch(saveMeetingParticipants({
      participants: transformedParticipants,
      intervalFetchAction: (payload === null || payload === void 0 ? void 0 : payload.intervalFetchAction) || false
    }));
    return response;
  }).catch(error => {
    // means api failed during interval fetch
    // but user still can use the app
    if ((payload === null || payload === void 0 ? void 0 : payload.intervalFetchAction) === undefined) {
      errorActions.setExternalOutage({
        isExternalOutage: true
      });
    }
    return thunkAPI.rejectWithValue(error);
  });
});
export const dissociateContact = createAsyncThunk('participants/dissociateContact', ({
  participantId,
  contactId
}, thunkAPI) => {
  const meetingUUID = meetingUUIDSelector(thunkAPI.getState()) || '';
  return thunkAPI.extra.participantsApi.removeParticipantMapping({
    participantId,
    contactId,
    meetingUid: meetingUUID
  }).then(() => {
    thunkAPI.dispatch(trackerActions.trackUsage(Object.assign({
      screen: SCREENS.participants
    }, ACTIONS.associateContact.unmap)));
    thunkAPI.dispatch(getMeetingParticipants());
  }).catch(error => {
    showApiAlert(error);
    return thunkAPI.rejectWithValue(error);
  });
});